import React, { useEffect, useState } from "react";
import "../mainContainer/MainContainer.css";
import "./ManageShopifyApp.css";
import { getUserId, getUserRole } from "../../utils/tokenUtils";
import TopBar from "../../components/TopBar/TopBar";
import { useToast } from "../../components/common/ToastManager/ToastContext";
import Sidebar from "../sidebar/Sidebar";
import SidebarBrand from "../sidebarBrand/SidebarBrand";
import { useNavigate, useSearchParams } from "react-router-dom";
import { HelenService } from "../../services/helen/helenService";
import { ThirdPartyAppsService } from "../../services/thirdPartyApps/thirdPartyAppsService";
import { ShopifyService } from "../../services/shopify/shopifyService";

const ManageShopifyApp: React.FC = () => {
  const [appName, setAppName] = useState("");

  const [bots, setBots] = useState<any[]>([]);
  const [selectedBot, setSelectedBot] = useState("");
  const [selectedStoreId, setSelectedStoreId] = useState("");

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const role = getUserRole();

  const handleBack = () => {
    navigate("/apps");
  };

  useEffect(() => {
    const fetchBots = async () => {
      try {
        const response = await HelenService.getBotsAssignedToUser("all");

        if (response.status === 200 && response.data?.userAssignedBots) {
          setBots(response.data.userAssignedBots);
        }
      } catch (error) {
        console.error("Failed to load Helens for apps", error);
      }
    };
    fetchBots();
  }, []);

  const appIdParam = searchParams.get("appId");
  const appId = appIdParam ? parseInt(appIdParam, 10) : null;

  useEffect(() => {
    if (!appId) return;

    const loadApp = async () => {
      try {
        setIsLoading(true);
        const response = await ShopifyService.getAppById(appId);
        const data = response.data;
        if (data) {
          setAppName(data.shop_domain || "");
          setSelectedBot(String(data.bot_id || ""));
          setSelectedStoreId(String(data.id || ""));
        }
      } catch (error) {
        console.error("Failed to load app details", error);
        showToast("Failed to load app details", "error");
      } finally {
        setIsLoading(false);
      }
    };

    loadApp();
  }, [appId, showToast]);

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!appName.trim()) newErrors.appName = "App name is required";
    if (!selectedBot) newErrors.selectedBot = "Please select a Helen";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const userId = getUserId();
    if (!userId) {
      showToast("User id not found. Please login again.", "error");
      return;
    }

    try {
      if (appId) {
        await ShopifyService.updateApp(selectedStoreId, Number(selectedBot));
        showToast("App updated successfully", "success");
      } else {
      }

      navigate("/apps");
    } catch (error: any) {
      const msg =
        error?.response?.data?.error ||
        "Failed to create app. Please try again.";
      showToast(msg, "error");
    }
  };

  if (!appId) {
    navigate("/apps");
  }

  return (
    <div className="dashboard-container">
      <TopBar />
      <div className="add-member-wrapper">
        <button className="back-link" onClick={handleBack}>
          <img src="/images/editarrow.svg" alt="Back" className="back-icon" />
          <span>Back To Apps</span>
        </button>
        <h2 className="add-member-title">Edit Shopify App</h2>
        <div className="edit-shopify-app-form-card">
          <form className="edit-shopify-app-form-grid" onSubmit={handleSubmit}>
            <div className="form-group">
              <label>
                Store Domain<span className="required"> *</span>
              </label>
              <input
                type="text"
                placeholder="Enter app name"
                value={appName}
                disabled={true}
                onChange={(e) => setAppName(e.target.value)}
              />
              {errors.appName && (
                <span className="error-text">{errors.appName}</span>
              )}
            </div>

            <div className="form-group">
              <label>
                Assign Helen<span className="required"> *</span>
              </label>
              <select
                value={selectedBot}
                onChange={(e) => setSelectedBot(e.target.value)}
              >
                <option value="">Select Helen</option>
                {bots.map((bot) => (
                  <option key={bot.id} value={bot.id}>
                    {bot?.helen_name
                      ? bot.helen_name.charAt(0).toUpperCase() +
                        bot.helen_name.slice(1).replace(/_/g, " ")
                      : "Unknown"}
                  </option>
                ))}
              </select>
              {errors.selectedBot && (
                <span className="error-text">{errors.selectedBot}</span>
              )}
            </div>

            <div className="form-buttons">
              <button
                className="cancel-button"
                type="button"
                onClick={handleBack}
              >
                Cancel
              </button>
              <button type="submit" className="submit-button">
                Update
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ManageShopifyApp;
